import React, { useContext, useEffect } from "react"
import { Query } from "react-apollo"
import gql from "graphql-tag"
import { Waypoint } from "react-waypoint"
import { Redirect } from "react-router"
import SearchBar from "../../components/SearchBar"
import ListCard from "./components/ListCard"
import UpsellModal from "../../components/UpsellModal"
import useDebounce from "../../hooks/useDebounce"
import AppContext from "../../shared/AppContext"

import "./drilldowns.scss"
import { Helmet } from "react-helmet"

const DRILLDOWNS_QUERY = gql`
  query SearchDrillDowns(
    $dataset: ModelDataset!
    $searchKeyword: String
    $offset: Int
    $sortOrder: SortDirection
    $sortField: ModelSortField
  ) {
    searchModels(
      searchKeyword: $searchKeyword
      dataset: $dataset
      sortOrder: $sortOrder
      sortField: $sortField
      offset: $offset
    ) {
      id
      displayName
      workspaceId
      reportId
      participantCount
      description
      launchDate
      roles
      vendors
      embargoEndDate
      slug
      product {
        id
        logo
      }
    }
  }
`

const DrillDowns = (props) => {
  const { isUserAuthenticated, roles, models = [] } = props.viewer
  const { trackEvent } = useContext(AppContext)

  if (!isUserAuthenticated) {
    return (
      <Redirect to={{ pathname: "/login", state: { from: "/drill-downs" } }} />
    )
  }

  const hasDrilldownRole = roles ? roles.includes("DrillDowns") : false
  const hasDrillDownModel = models.filter((model) => {
    return model.dataset.toLowerCase() === "drilldown"
  })

  if (!hasDrilldownRole && !hasDrillDownModel.length) {
    return <UpsellModal />
  }

  const {
    value: searchKeyword,
    deferredValue: deferredKeyword,
    setValue: setKeyword,
  } = useDebounce(250)

  const trackSearchKeyword = () => {
    trackEvent("Drilldown Search", {
      keywordSearch: deferredKeyword,
    })
  }

  useEffect(trackSearchKeyword, [deferredKeyword])

  const onLoadMoreDrillDowns = (data, fetchMore) => {
    const currentLength = data.searchModels.length

    fetchMore({
      variables: {
        offset: currentLength,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return Object.assign({}, prev, {
          searchModels: [...prev.searchModels, ...fetchMoreResult.searchModels],
        })
      },
    })
  }

  const globalWindow = typeof window !== "undefined" ? window : undefined

  return (
    <div className="drilldowns-list-page-container">
      <Helmet>
        <title>Drill Downs | ETR</title>
      </Helmet>
      <SearchBar
        classes={["border", "rounded"]}
        placeholder={"Search by drilldown, date, company or sector"}
        value={searchKeyword}
        onChange={(e) => {
          setKeyword(e.target.value)
        }}
      />
      <Query
        query={DRILLDOWNS_QUERY}
        fetchPolicy="network-only"
        variables={{
          sortOrder: "desc",
          sortField: "launchDate",
          searchKeyword: deferredKeyword,
          offset: 0,
          dataset: "DRILLDOWN",
        }}
      >
        {({ loading, error, data, fetchMore }) => {
          if (loading) return <div>Loading...</div>
          if (error) {
            console.log(error)
            return <div>An error has occurred while searching drill downs.</div>
          }

          if (!data || !data.searchModels || !data.searchModels.length) {
            return (
              <div>
                <b data-cy="no-results">No search results found.</b>
              </div>
            )
          }

          const isAdmin = props.viewer.roles.find((role) =>
            role.includes("Admin")
          )

          return (
            <div
              className="drilldown-results-list"
              data-cy={"drilldown-results-list"}
            >
              {data.searchModels.map((model, idx) => {
                const modelLogo = model.product ? model.product.logo : null
                return (
                  <React.Fragment key={model.id}>
                    <ListCard
                      data-cy="model-listcard"
                      thumbnail={modelLogo}
                      model={model}
                      isAdmin={isAdmin}
                      keywordSearch={deferredKeyword}
                    />
                    {idx === data.searchModels.length - 5 && (
                      <Waypoint
                        scrollableAncestor={globalWindow}
                        onEnter={() => onLoadMoreDrillDowns(data, fetchMore)}
                      />
                    )}
                  </React.Fragment>
                )
              })}
            </div>
          )
        }}
      </Query>
    </div>
  )
}

export default DrillDowns
